// src/core/adapters/LondonArrivalsBoardData.js
//
// Display-time data preparation for the arrivals board. Builds lookups
// from raw arrivals / expected arrivals, and resolves per-cell headway
// status into display colours.

import { statusFromBaseline } from '@core/domain/status';
import { capitalize } from '@core/domain/strings';
import { arrivalPresentation } from '@core/config/arrivalPresentation';

// Extracts the London-time hour (0-23) from an ISO string.
export const getLondonHour = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;
  const hourStr = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    hour12: false,
  }).format(d);
  return parseInt(hourStr, 10);
};

const makeTripStopKey = (tripNoLbsl, naptanId) =>
  `${String(tripNoLbsl).trim()}-${String(naptanId).trim()}`;

// Lookup of raw headway values (seconds) keyed by trip-stop.
export const buildHeadwayLookup = (rawArrivals) => {
  const lookup = {};
  rawArrivals.forEach(item => {
    const key = makeTripStopKey(item.trip_no_lbsl, item.naptan_id);
    if (item.headway !== undefined && item.headway !== null) {
      lookup[key] = item.headway;
    }
  });
  return lookup;
};

// Lookup of per-hour average headway (minutes) keyed by hour-naptan.
export const buildAvgHeadwayLookup = (rawArrivals) => {
  const groups = {};
  rawArrivals.forEach(item => {
    const hour = getLondonHour(item.expected_arrival);
    if (hour === null) return;
    const key = `${hour}-${String(item.naptan_id).trim()}`;
    if (!groups[key]) groups[key] = [];
    if (item.headway !== undefined && item.headway !== null && !isNaN(item.headway)) {
      groups[key].push(item.headway / 60);
    }
  });
  const avgLookup = {};
  Object.entries(groups).forEach(([key, values]) => {
    if (values.length > 0) {
      const sum = values.reduce((a, b) => a + b, 0);
      avgLookup[key] = sum / values.length;
    }
  });
  return avgLookup;
};

// Lookup of expected (future) arrival times keyed by trip-stop.
export const buildFutureLookup = (expectedArrivals) => {
  const map = new Map();
  (expectedArrivals || []).forEach(item => {
    const key = makeTripStopKey(item.trip_no_lbsl, item.naptan_id);
    map.set(key, item.expected_arrival);
  });
  return map;
};

// Lookup of headway to the previous stop (seconds) for future arrivals.
export const buildFutureHeadwayLookup = (rawArrivals, expectedArrivals, activeDir) => {
  const map = new Map();
  const rawGroups = {};
  (rawArrivals || [])
    .filter(item => item.direction === activeDir)
    .forEach(item => {
      const naptan = String(item.naptan_id).trim();
      if (!rawGroups[naptan]) rawGroups[naptan] = [];
      rawGroups[naptan].push({
        time: new Date(item.expected_arrival),
        trip: String(item.trip_no_lbsl).trim(),
        type: 'raw',
      });
    });

  const futureGroups = {};
  (expectedArrivals || [])
    .filter(item => item.direction === activeDir)
    .forEach(item => {
      const naptan = String(item.naptan_id).trim();
      if (!futureGroups[naptan]) futureGroups[naptan] = [];
      futureGroups[naptan].push({
        time: new Date(item.expected_arrival),
        trip: String(item.trip_no_lbsl).trim(),
        type: 'future',
      });
    });

  const allNaptans = new Set([...Object.keys(rawGroups), ...Object.keys(futureGroups)]);
  allNaptans.forEach(naptan => {
    const combined = [
      ...(rawGroups[naptan] || []),
      ...(futureGroups[naptan] || []),
    ];
    combined.sort((a, b) => a.time - b.time);
    for (let i = 0; i < combined.length; i++) {
      const item = combined[i];
      if (item.type === 'future' && i > 0) {
        const prev = combined[i - 1];
        const diffSec = (item.time - prev.time) / 1000;
        const key = makeTripStopKey(item.trip, naptan);
        map.set(key, diffSec);
      }
    }
  });
  return map;
};

// Default headway colour mapping, sourced from the shared presentation config
// so a client can restyle headways by overriding @core/config/arrivalPresentation.
// Exported so the component can seed state from it when UI-driven theming arrives.
export const DEFAULT_HEADWAY_COLORS = {
  tight: arrivalPresentation.headway.tight,
  normal: arrivalPresentation.headway.normal,
  large: arrivalPresentation.headway.large,
};

const HEADWAY_FALLBACK = arrivalPresentation.headway.fallback;

// Resolves the display colours for a headway cell.
export const resolveHeadwayColors = (
  headwaySeconds,
  baselineMin,
  toleranceMin,
  colorMap,
  isFuture
) => {
  if (headwaySeconds === null || headwaySeconds === undefined || isNaN(headwaySeconds)) {
    return HEADWAY_FALLBACK;
  }
  if (baselineMin === null || baselineMin === undefined || isNaN(baselineMin)) {
    return HEADWAY_FALLBACK;
  }

  const headwayMin = headwaySeconds / 60;
  const status = statusFromBaseline(headwayMin, baselineMin, toleranceMin);

  let entry = HEADWAY_FALLBACK;
  if (status === 'low') entry = colorMap.tight;
  else if (status === 'high') entry = colorMap.large;
  else if (status === 'normal') entry = colorMap.normal;

  return {
    color: entry.color,
    bg: isFuture ? 'transparent' : entry.bg,
  };
};

// ---- SlidePanel helpers ----

// Normalizes a stop's direction (string 'outbound'/'inbound' or numeric 1/2)
// to a lowercase lane direction, or null when unknown.
export const deriveLaneDirection = (stopData) => {
  if (typeof stopData?.direction === 'string') return stopData.direction.toLowerCase();
  if (stopData?.direction === 1) return 'outbound';
  if (stopData?.direction === 2) return 'inbound';
  return null;
};

// Merges raw board stop data with enriched panel data from useStopArrival.
// Enriched values win where present; raw board values fill the gaps.
export const buildPanelData = (stopData, data) => {
  const laneDirection = deriveLaneDirection(stopData);
  return {
    ...stopData,
    stopName: data?.stopName || stopData?.name || 'Unknown stop',
    timingPointCode: data?.timingPointCode || stopData?.timing_point_code || '',
    direction: laneDirection
      ? capitalize(laneDirection)
      : data?.direction || 'Inbound',
    trackColor: stopData?.trackColor
      || (laneDirection === 'inbound' ? 'var(--ib-track)' : 'var(--ob-track)'),
    arrivals: data?.arrivals || [],
    schedules: data?.schedules || [],
  };
};

// Builds a trip_no_lbsl → HH:MM schedule lookup.
export const buildScheduleMap = (schedules) => {
  const map = {};
  schedules.forEach(item => {
    map[item.trip_no_lbsl] = item.arrival_time?.slice(0, 5) || '--:--';
  });
  return map;
};